// @ts-nocheck
import React, { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { invokeEdgeFunction } from '@/lib/invokeEdgeFunction';
import { toast } from 'sonner';
import ModuleGuard from '@/components/common/ModuleGuard';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Loader2, Plus } from 'lucide-react';
import { formatCalendarRange } from '@/lib/dates';

const FN = 'rle-manage-staff';

// The only three staff roles, and the permissions each one ticks by default.
const STAFF_ROLES = [
  { value: 'Scorekeeper', hint: 'Scoring', scoring: true, check_in: false },
  { value: 'Check-in', hint: 'Check-in', scoring: false, check_in: true },
  { value: 'Event Staff', hint: 'Scoring + Check-in', scoring: true, check_in: true },
];
const isStaffRole = (v) => STAFF_ROLES.some((r) => r.value === v);

/**
 * One grid template, shared by the desktop column header and every assignment
 * row. Both are separate grid containers, so any track that was `auto` was
 * sized on its own in each container and the values drifted out from under
 * their header — every track here is fixed, so both containers resolve the
 * same columns. The last (actions) column deliberately has no header.
 */
const ASSIGN_COLS = 'minmax(220px, 2fr) 140px 140px minmax(160px, 1fr) 170px';

type Staff = {
  id: string;
  name: string;
  email: string;
  phone?: string | null;
  role?: string | null;
  user_id?: string | null;
  status?: string | null;
  assignments?: Array<{
    id: string;
    event_id: string;
    event_name?: string | null;
    court_id?: string | null;
    court_name?: string | null;
    role?: string | null;
    permissions?: { scoring?: boolean; check_in?: boolean } | null;
  }>;
};

async function callStaffFn(body: any) {
  const { data, error } = await invokeEdgeFunction(FN, { body });
  if (error) throw new Error(error.message || 'Request failed');
  // rle-manage-staff wraps every response:
  //   { ok: true,  data: { staff, assignments } }  for list
  //   { ok: true,  data: { staff } }               for create / update / link_user
  //   { ok: true,  data: { assignment } }          for assign
  //   { ok: false, error: { code, message } }      on error
  if (data && data.ok === false) {
    throw new Error(data.error?.message || data.error || 'Request failed');
  }
  // Unwrap the envelope so callers always work with the inner payload.
  return data?.data ?? data;
}

const StaffAdminPageInner: React.FC = () => {
  const { user, profile } = useAuth();

  // Resolve orgs from active memberships (same source as the admin org switcher);
  // profile.organization_id is often null for org admins.
  const orgsQuery = useQuery({
    queryKey: ['rle-staff-orgs', user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('organization_members')
        .select('organization_id, organizations:organization_id(id, name)')
        .eq('user_id', user.id)
        .eq('status', 'active');
      if (error) throw error;
      const orgs = (data || [])
        .map((m: any) => m.organizations)
        .filter(Boolean)
        .filter((o: any, i: number, a: any[]) => a.findIndex((x) => x.id === o.id) === i);
      if (profile?.organization_id && !orgs.some((o: any) => o.id === profile.organization_id)) {
        const { data: po } = await supabase.from('organizations').select('id, name').eq('id', profile.organization_id).maybeSingle();
        if (po) orgs.push(po);
      }
      const counts = await Promise.all(
        orgs.map(async (o: any) => {
          const { count } = await supabase
            .from('events')
            .select('id', { count: 'exact', head: true })
            .eq('organization_id', o.id);
          return { ...o, eventCount: count ?? 0 };
        })
      );
      return counts.sort((a, b) => b.eventCount - a.eventCount);
    },
  });
  const orgs = Array.isArray(orgsQuery.data) ? orgsQuery.data : [];
  const [pickedOrgId, setPickedOrgId] = useState<string | null>(null);
  const orgId = (pickedOrgId && orgs.some((o) => o.id === pickedOrgId) ? pickedOrgId : orgs[0]?.id) ?? null;
  const qc = useQueryClient();

  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState<Staff | null>(null);
  const [assigning, setAssigning] = useState<Staff | null>(null);
  const [linking, setLinking] = useState<Staff | null>(null);
  const [removing, setRemoving] = useState<{ s: Staff; a: any } | null>(null);
  const [busy, setBusy] = useState(false);

  const staffQuery = useQuery({
    queryKey: ['rle-staff', orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const payload = await callStaffFn({ action: 'list', organization_id: orgId });
      const staffArr = Array.isArray(payload?.staff) ? payload.staff : [];
      const topAssignments = Array.isArray(payload?.assignments) ? payload.assignments : null;
      if (!topAssignments) {
        return staffArr.map((s: any) => ({
          ...s,
          assignments: Array.isArray(s?.assignments) ? s.assignments : [],
        }));
      }
      // The function returns assignments as a flat list — group them by staff
      // and join event / court names, since the function returns ids only.
      const byStaff = new Map<string, any[]>();
      const eventIds = new Set<string>();
      const courtIds = new Set<string>();
      for (const a of topAssignments) {
        if (!a?.staff_id) continue;
        const list = byStaff.get(a.staff_id) ?? [];
        list.push(a);
        byStaff.set(a.staff_id, list);
        if (a.event_id) eventIds.add(a.event_id);
        if (a.court_id) courtIds.add(a.court_id);
      }
      const [eventsRes, courtsRes] = await Promise.all([
        eventIds.size
          ? supabase.from('events').select('id, name').in('id', Array.from(eventIds))
          : Promise.resolve({ data: [] as any[], error: null }),
        courtIds.size
          ? supabase.from('event_courts').select('id, name').in('id', Array.from(courtIds))
          : Promise.resolve({ data: [] as any[], error: null }),
      ]);
      const eventName = new Map<string, string>();
      for (const e of (Array.isArray(eventsRes.data) ? eventsRes.data : [])) eventName.set(e.id, e.name);
      const courtName = new Map<string, string>();
      for (const c of (Array.isArray(courtsRes.data) ? courtsRes.data : [])) courtName.set(c.id, c.name);
      return staffArr.map((s: any) => ({
        ...s,
        assignments: (byStaff.get(s.id) ?? []).map((a: any) => ({
          ...a,
          event_name: eventName.get(a.event_id) || a.event_id,
          court_name: a.court_id ? (courtName.get(a.court_id) || a.court_id) : null,
        })),
      }));
    },
  });

  const eventsQuery = useQuery({
    queryKey: ['rle-staff-events', orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('events')
        .select('id, name, start_date, end_date')
        .eq('organization_id', orgId)
        .order('start_date', { ascending: false })
        .limit(200);
      if (error) throw error;
      return Array.isArray(data) ? data : [];
    },
  });

  const refresh = () => qc.invalidateQueries({ queryKey: ['rle-staff', orgId] });

  const run = async (body: any, successMsg: string) => {
    setBusy(true);
    try {
      await callStaffFn({ organization_id: orgId, ...body });
      toast.success(successMsg);
      refresh();
      return true;
    } catch (e: any) {
      toast.error(e.message || 'Something went wrong');
      return false;
    } finally {
      setBusy(false);
    }
  };

  const staff = Array.isArray(staffQuery.data) ? staffQuery.data : [];

  const eventDates = useMemo(() => {
    const m = new Map<string, any>();
    for (const e of (Array.isArray(eventsQuery.data) ? eventsQuery.data : [])) m.set(e.id, e);
    return m;
  }, [eventsQuery.data]);
  const eventDatesLabel = (a) => {
    const e = eventDates.get(a.event_id);
    if (!e) return '';
    const range = formatCalendarRange(e.start_date, e.end_date, '');
    // The helper spaces a same-month range ("Oct 10 – 11, 2026"); event lines
    // read tighter, the way they do everywhere else ("Oct 10–11, 2026").
    const m = range.match(/^([A-Za-z]{3} \d{1,2}) – (\d{1,2}, \d{4})$/);
    return m ? `${m[1]}–${m[2]}` : range;
  };

  // Sort assignment rows by event start date, soonest first.
  const sortAssignments = (list) =>
    [...list].sort((x, y) => {
      const dx = eventDates.get(x.event_id)?.start_date || '';
      const dy = eventDates.get(y.event_id)?.start_date || '';
      return String(dx).localeCompare(String(dy));
    });

  const AssignmentRow = ({ s, a }) => (
    <div
      className="flex flex-col gap-2 border-b py-3 last:border-b-0 sm:grid sm:items-center sm:gap-4 sm:py-2.5"
      style={{ gridTemplateColumns: ASSIGN_COLS }}
    >
      <div className="min-w-0">
        <p className="font-medium leading-tight">{a.event_name || a.event_id}</p>
        {eventDatesLabel(a) && (
          <p className="text-xs text-muted-foreground">{eventDatesLabel(a)}</p>
        )}
      </div>
      <div className="flex items-center gap-2 sm:contents">
        <p className="text-sm sm:truncate">
          {a.court_id ? `${a.court_name || 'Court'} only` : 'Any court'}
        </p>
        <span className="text-muted-foreground sm:hidden">·</span>
        <p className="text-sm sm:truncate">{a.role || '—'}</p>
      </div>
      <div className="flex flex-wrap gap-1">
        {a.permissions?.scoring === true && <Badge variant="outline">Scoring</Badge>}
        {a.permissions?.check_in === true && <Badge variant="outline">Check-in</Badge>}
      </div>
      <div className="flex gap-2 sm:justify-end">
        <Button
          variant="outline"
          size="sm"
          className="h-11 flex-1 sm:flex-none"
          disabled={busy}
          onClick={() => setAssigning({ staff: s, assignment: a })}
        >
          Edit
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="h-11 flex-1 sm:flex-none"
          disabled={busy}
          onClick={() => setRemoving({ s, a })}
        >
          Remove
        </Button>
      </div>
    </div>
  );

  const StaffCard = ({ s }) => (
    <div className="space-y-4 rounded-lg border p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-baseline gap-x-2">
            <p className="font-semibold">{s.name}</p>
            <span className="truncate text-sm text-muted-foreground">{s.email}</span>
          </div>
          <p className="text-sm text-muted-foreground">
            {s.role || '—'} · {s.user_id ? 'Linked' : 'No login yet'}
          </p>
        </div>
        <div className="flex flex-wrap gap-2 sm:shrink-0 sm:justify-end">
          <Button variant="outline" size="sm" className="h-11" onClick={() => setEditing(s)}>
            Edit person
          </Button>
          <Button size="sm" className="h-11" onClick={() => setAssigning({ staff: s })}>
            + Assign to event
          </Button>
          {!s.user_id && (
            <Button variant="outline" size="sm" className="h-11" onClick={() => setLinking(s)}>
              Link account
            </Button>
          )}
        </div>
      </div>
      {(s.assignments?.length ?? 0) === 0 ? (
        <div className="flex flex-col gap-3 border-t pt-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-muted-foreground">Not assigned to any events yet</p>
          <Button variant="outline" size="sm" className="h-11" onClick={() => setAssigning({ staff: s })}>
            + Assign to event
          </Button>
        </div>
      ) : (
        <div className="border-t overflow-x-auto">
          {/* Desktop column headers — same grid template as every row below */}
          <div
            className="hidden gap-4 py-2 text-xs font-medium text-muted-foreground sm:grid sm:items-center"
            style={{ gridTemplateColumns: ASSIGN_COLS }}
          >
            <span>Event</span>
            <span>Court</span>
            <span>Role</span>
            <span>Can do</span>
            <span aria-hidden="true" />
          </div>
          {sortAssignments(s.assignments!).map((a) => (
            <AssignmentRow key={a.id} s={s} a={a} />
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-primary">Staff</h1>
          <p className="text-muted-foreground">
            Event-day staff, their accounts, and what they can do at each event.
          </p>
        </div>
        <Button onClick={() => setAddOpen(true)} disabled={!orgId}>
          <Plus className="h-4 w-4 mr-2" /> Add staff
        </Button>
      </div>

      {orgs.length > 1 && (
        <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-3">
          <Label>Organization</Label>
          <Select value={orgId ?? ''} onValueChange={setPickedOrgId}>
            <SelectTrigger className="w-full sm:w-[320px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              {orgs.map((o) => (
                <SelectItem key={o.id} value={o.id}>{o.name} ({o.eventCount} events)</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Staff members</CardTitle>
        </CardHeader>
        <CardContent>
          {orgsQuery.isLoading || (!!orgId && staffQuery.isLoading) ? (
            <div className="flex justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : orgsQuery.error ? (
            <p className="py-8 text-center text-sm text-destructive">
              Could not load your organizations: {(orgsQuery.error as any).message}
            </p>
          ) : !orgId ? (
            <p className="py-8 text-center text-sm text-destructive">
              No organization found for your account, so staff can't be listed. Ask a platform admin to add you to an organization.
            </p>
          ) : staffQuery.isLoading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : staffQuery.error ? (
            <p className="py-8 text-center text-sm text-destructive">
              {(staffQuery.error as any).message}
            </p>
          ) : staff.length === 0 ? (
            <p className="py-10 text-center text-sm text-muted-foreground">
              No staff yet. Add your first staff member to get started.
            </p>
          ) : (
              <div className="space-y-4">
                {staff.map((s) => (
                  <StaffCard key={s.id} s={s} />
                ))}
              </div>

          )}
        </CardContent>
      </Card>

      <StaffFormDialog
        open={addOpen || !!editing}
        staff={editing}
        busy={busy}
        onClose={() => {
          setAddOpen(false);
          setEditing(null);
        }}
        onSubmit={async (values) => {
          const ok = editing
            ? await run({ action: 'update', staff_id: editing.id, ...values }, 'Staff updated')
            : await run({ action: 'create', ...values }, 'Staff added');
          if (ok) {
            setAddOpen(false);
            setEditing(null);
          }
        }}
      />

      <AssignDialog
        target={assigning}
        events={Array.isArray(eventsQuery.data) ? eventsQuery.data : []}
        busy={busy}
        onClose={() => setAssigning(null)}
        onSubmit={async (values) => {
          const old = assigning?.assignment;
          const ok = await run(
            { action: 'assign', staff_id: assigning!.staff.id, ...values },
            old ? 'Assignment updated' : 'Assigned to event'
          );
          if (!ok) return;
          if (old && (old.court_id || null) !== (values.court_id || null)) {
            await run({ action: 'unassign', assignment_id: old.id }, 'Old court assignment removed');
          }
          setAssigning(null);
        }}
      />


      <LinkAccountDialog
        staff={linking}
        busy={busy}
        onClose={() => setLinking(null)}
        onSubmit={async (email) => {
          const ok = await run({ action: 'link_user', staff_id: linking!.id, email }, 'Account linked');
          if (ok) setLinking(null);
        }}
      />

      <AlertDialog open={!!removing} onOpenChange={(o) => !o && setRemoving(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove assignment?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove {removing?.s.name}'s assignment to{' '}
              {removing?.a.event_name || 'this event'}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const r = removing;
                setRemoving(null);
                if (r) run({ action: 'unassign', assignment_id: r.a.id }, 'Assignment removed');
              }}
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

/* ---------------- dialogs ---------------- */

const RoleSelect = ({ value, onChange }) => {
  const isKnown = STAFF_ROLES.some((r) => r.value === value);
  return (
    <Select value={value || ''} onValueChange={onChange}>
      <SelectTrigger className="h-11"><SelectValue placeholder="Choose a role" /></SelectTrigger>
      <SelectContent>
        {value && !isKnown && (
          <SelectItem value={value} disabled>{value} (pick one below)</SelectItem>
        )}
        {STAFF_ROLES.map((r) => (
          <SelectItem key={r.value} value={r.value}>{r.value} — {r.hint}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

const StaffFormDialog = ({ open, staff, busy, onClose, onSubmit }) => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', role: '' });

  React.useEffect(() => {
    if (open) {
      setForm({
        name: staff?.name || '',
        email: staff?.email || '',
        phone: staff?.phone || '',
        role: staff?.role || '',
      });
    }
  }, [open, staff]);

  const roleOk = isStaffRole(form.role);
  const reason = !form.name || !form.email ? 'Enter a name and email' : !roleOk ? 'Pick a role' : '';

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{staff ? 'Edit staff member' : 'Add staff member'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div>
            <Label>Email</Label>
            <Input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
          </div>
          <div>
            <Label>Phone</Label>
            <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </div>
          <div>
            <Label>Role</Label>
            <RoleSelect value={form.role} onChange={(v) => setForm({ ...form, role: v })} />
            {form.role && !roleOk && (
              <p className="mt-1 text-xs text-destructive">
                “{form.role}” isn't a current role. Pick Scorekeeper, Check-in or Event Staff.
              </p>
            )}
          </div>
        </div>
        <DialogFooter className="gap-2">
          {reason && <p className="text-sm text-muted-foreground sm:mr-auto sm:self-center">{reason}</p>}
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={busy || !!reason} onClick={() => onSubmit(form)}>
            {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {staff ? 'Save' : 'Add staff'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const AssignDialog = ({ target, events, busy, onClose, onSubmit }) => {
  const staff = target?.staff;
  const assignment = target?.assignment;
  const [eventId, setEventId] = useState('');
  const [courtId, setCourtId] = useState('all');
  const [role, setRole] = useState('');
  const [scoring, setScoring] = useState(false);
  const [checkIn, setCheckIn] = useState(false);

  React.useEffect(() => {
    if (!target) return;
    if (assignment) {
      setEventId(assignment.event_id || '');
      setCourtId(assignment.court_id || 'all');
      setRole(assignment.role || staff?.role || '');
      setScoring(assignment.permissions?.scoring === true);
      setCheckIn(assignment.permissions?.check_in === true);
    } else {
      const r = STAFF_ROLES.find((x) => x.value === staff?.role);
      setEventId('');
      setCourtId('all');
      setRole(staff?.role || '');
      setScoring(r ? r.scoring : false);
      setCheckIn(r ? r.check_in : false);
    }
  }, [target]);

  const pickRole = (v) => {
    setRole(v);
    const r = STAFF_ROLES.find((x) => x.value === v);
    if (r) {
      setScoring(r.scoring);
      setCheckIn(r.check_in);
    }
  };

  const courtsQuery = useQuery({
    queryKey: ['event-courts', eventId],
    enabled: !!eventId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('event_courts')
        .select('id, name')
        .eq('event_id', eventId)
        .order('name');
      if (error) throw error;
      return data || [];
    },
  });

  const reason = !eventId
    ? 'Choose an event'
    : !isStaffRole(role)
    ? 'Pick a role'
    : !scoring && !checkIn
    ? 'Tick Scoring or Check-in'
    : '';

  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {assignment ? `Edit ${staff?.name}'s assignment` : `Assign ${staff?.name} to an event`}
          </DialogTitle>
          <DialogDescription>
            Leave on Any court to let them work every court at this event.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Event</Label>
            <Select value={eventId} onValueChange={(v) => { setEventId(v); setCourtId('all'); }} disabled={!!assignment}>
              <SelectTrigger className="h-11"><SelectValue placeholder="Select an event" /></SelectTrigger>
              <SelectContent>
                {events.map((e: any) => (
                  <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Court</Label>
            <Select value={courtId} onValueChange={setCourtId} disabled={!eventId}>
              <SelectTrigger className="h-11"><SelectValue placeholder="Any court" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any court</SelectItem>
                {(Array.isArray(courtsQuery.data) ? courtsQuery.data : []).map((c: any) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Role at this event</Label>
            <RoleSelect value={role} onChange={pickRole} />
          </div>
          <div className="space-y-2">
            <Label>Permissions</Label>
            <label className="flex min-h-11 items-center gap-3 text-sm">
              <Checkbox checked={scoring} onCheckedChange={(v) => setScoring(!!v)} />
              Scoring
            </label>
            <label className="flex min-h-11 items-center gap-3 text-sm">
              <Checkbox checked={checkIn} onCheckedChange={(v) => setCheckIn(!!v)} />
              Check-in
            </label>
          </div>
        </div>
        <DialogFooter className="gap-2">
          {reason && <p className="text-sm text-muted-foreground sm:mr-auto sm:self-center">{reason}</p>}
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            disabled={busy || !!reason}
            onClick={() =>
              onSubmit({
                event_id: eventId,
                court_id: courtId === 'all' ? null : courtId,
                role,
                permissions: { scoring: scoring === true, check_in: checkIn === true },
              })
            }
          >
            {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {assignment ? 'Save' : 'Assign'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const LinkAccountDialog = ({ staff, busy, onClose, onSubmit }) => {
  const [email, setEmail] = useState('');
  React.useEffect(() => { if (staff) setEmail(staff.email || ''); }, [staff]);

  return (
    <Dialog open={!!staff} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Link an account</DialogTitle>
          <DialogDescription>
            Links {staff?.name} to an existing user account with this email. They can also simply
            sign up with this email and the account links automatically.
          </DialogDescription>
        </DialogHeader>
        <div>
          <Label>Email</Label>
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={busy || !email} onClick={() => onSubmit(email)}>
            {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Link account
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const StaffAdminPage: React.FC = () => (
  <ModuleGuard module="staff_portal">
    <StaffAdminPageInner />
  </ModuleGuard>
);

export default StaffAdminPage;
